// The dialog that names a device: the organization's label, which its
// administrators and MotionSafe staff set, or MotionSafe's own staff label,
// which no organization sees. One field, named by the title; under it what
// the label is for and how much of its length is used. Left empty, it clears
// the label.

import { Button, Group, Modal, Stack, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { Device } from '@motionsafe/ms-console-api/admin/v1/device_pb.js';
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { api } from '../api/client';
import { errorText, useInvalidate } from '../api/hooks';
import { DeviceLine } from './device-line';
import { DialogLoadBar } from './top-load-bar';

export const LABEL_MAX = 40;

export type LabelKind = 'label' | 'staff';

type LabelProps = { device: Device; kind: LabelKind; /** The reader is MotionSafe staff. */ staff: boolean; onClose: () => void };

export function LabelDialog({ opened, round, ...props }: LabelProps & { opened: boolean; round: number }) {
  return (
    <Modal opened={opened} onClose={props.onClose} title={props.kind === 'staff' ? 'Staff label' : 'Device label'} size={440}>
      <DialogLoadBar />
      <LabelForm key={round} {...props} />
    </Modal>
  );
}

function LabelForm({ device, kind, staff, onClose }: LabelProps) {
  const inv = useInvalidate();
  const current = kind === 'staff' ? device.staffLabel : device.label;
  const [value, setValue] = useState(current);
  const [error, setError] = useState('');
  const next = value.replace(/\s+/g, ' ').trim();
  const save = useMutation({
    mutationFn: async () => {
      if (kind === 'staff') await api.device.setDeviceStaffLabel({ deviceId: device.id, staffLabel: next });
      else await api.device.setDeviceLabel({ deviceId: device.id, label: next });
    },
    onSuccess: () => {
      notifications.show({ message: next ? `Device labelled ${next}.` : 'Label removed.' });
      onClose();
      return inv('devices');
    },
    onError: (e) => setError(errorText(e)),
  });
  const ready = next !== current && !save.isPending;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) { setError(''); save.mutate(); }
  };
  const note = kind === 'staff'
    ? 'Only MotionSafe sees it. It stays with the device when it moves.'
    : staff
      ? 'The organization\'s own name for it, which its administrators change too.'
      : 'Your organization\'s name for it. MotionSafe sees it too.';
  const used = [...value].length;
  return (
    <form onSubmit={submit}>
      <Stack gap="lg">
        <DeviceLine device={device} />
        <TextInput
          aria-label={kind === 'staff' ? 'Staff label' : 'Device label'}
          placeholder={kind === 'staff' ? 'Unit 0042' : 'Patrol 14'}
          // What it is for, and the length against its limit, under the field.
          inputWrapperOrder={['input', 'description', 'error']}
          description={<><span>{note}</span><span className="num" style={{ flex: 'none' }}>{used} of {LABEL_MAX}</span></>}
          styles={{ description: { display: 'flex', justifyContent: 'space-between', gap: 'var(--mantine-spacing-md)', marginTop: 8 } }}
          data-autofocus
          maxLength={LABEL_MAX}
          autoComplete="off"
          value={value}
          onChange={(e) => { setValue(e.currentTarget.value); setError(''); }}
          error={error || undefined}
        />
        <Group className="dialog-footer" justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!ready || save.isPending}>{!next && current ? 'Remove label' : 'Save label'}</Button>
        </Group>
      </Stack>
    </form>
  );
}
